'use client'
import * as React from 'react'
import Link from 'next/link'
import { Globe, ChevronDown, Check, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { locales } from '@/lib/copy/locales'
import { useTranslation } from '@/lib/i18n/use-translation'
import type { LocaleCode } from '@/lib/i18n/dict'

// Footer language switcher. The four ready locales (en / es / fr / de)
// swap the page dictionary via cookie + react re-render, no path
// change, no SSR cache miss. The six non-ready locales render as
// "Soon" rows with the language tag tooltip; clicking them does
// nothing until the matching dict ships.
export function LanguageSwitcher() {
  const { locale, setLocale, t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey) }
  }, [open])

  const current = locales.find((l) => l.code === locale) || locales[0]

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-xs font-medium transition hover:border-primary/40"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t.switcher.label}: ${current.label}`}
      >
        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
        <span>{current.flag}</span>
        <span>{current.label}</span>
        <ChevronDown className={cn('h-3 w-3 transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-40 mb-2 w-64 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl"
        >
          <ul className="py-1.5">
            {locales.map((l) => {
              const isActive = locale === l.code
              if (l.ready) {
                return (
                  <li key={l.code} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setLocale(l.code as LocaleCode)
                        setOpen(false)
                      }}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-secondary',
                        isActive && 'bg-secondary/60',
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span>{l.flag}</span>
                        <span>{l.label}</span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.code}</span>
                      </span>
                      {isActive && <Check className="h-3.5 w-3.5 text-primary" />}
                    </button>
                  </li>
                )
              }
              return (
                <li key={l.code} role="none">
                  <div
                    aria-disabled
                    title={t.switcher.soonTooltip}
                    className="flex cursor-not-allowed items-center justify-between gap-3 px-3 py-2 text-sm opacity-55"
                  >
                    <span className="flex items-center gap-2">
                      <span>{l.flag}</span>
                      <span>{l.label}</span>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.code}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700">
                      <Sparkles className="h-2.5 w-2.5" />
                      {t.switcher.soon}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
          <div className="flex items-center justify-between border-t bg-secondary/30 px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.switcher.caption}
            </p>
            <Link href="/answer-engine" className="text-[10px] font-medium text-muted-foreground hover:text-foreground">
              {t.switcher.playLink}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
